import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { CreateSnapshotDto, Snapshot } from 'types';
import { AnalyzerMode } from '../../analyzers/types';
import { updateAnalyzerProperties } from '../../spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { useCallback } from 'react';

import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useSnapshots } from 'features/snapshots/api/getSnapshots';
import { useUpdateAnalyzer } from 'features/analyzers/api/updateAnalyzer';
import { useUpdateSnapshot } from 'features/snapshots/api/updateSnapshot';
import { useDeleteSnapshot } from 'features/snapshots/api/deleteSnapshot';
import { useCreateSnapshot } from 'features/snapshots/api/createSnapshot';
import { useIsGeoEnabled } from 'features/analyzerModules/api/getAnalyzerModules';

const useHandleCrossPolClicked = (
  snapshotId: number | undefined,
  analyzerId: number
) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { data: selectedAnalyzer } = useAnalyzer({ analyzerId });
  const { data: allSnapshotsForAnalyzer } = useSnapshots({ analyzerId });
  const { data: selectedSnapshot } = useSnapshot({
    snapshotId: snapshotId || -1,
  });
  const updateAnalyzerMutation = useUpdateAnalyzer({});
  const createSnapshotMutation = useCreateSnapshot({
    analyzerId,
  });
  const updateSnapshotMutation = useUpdateSnapshot({
    config: { onMutate: () => {} },
  });
  const deleteSnapshotMutation = useDeleteSnapshot({ analyzerId });

  const geoEnabled = useIsGeoEnabled(selectedAnalyzer?.id);

  return useCallback(async () => {
    const turnOffAllOtherPollingSnapshots = (savedSnapshot: Snapshot) => {
      if (allSnapshotsForAnalyzer) {
        for (const snapshot of allSnapshotsForAnalyzer) {
          if (snapshot.id !== snapshotId && snapshot.id !== savedSnapshot.id)
            updateSnapshotMutation.mutate({
              snapshotId: snapshot.id,
              data: { enabled: false, temporarily_disabled: true },
            });
        }
      }
    };
    const switchAnalyzerToPollingAndSavePriorMode = async () => {
      if (!selectedAnalyzer) return;
      const analyzerUpdate = {
        id: selectedAnalyzer.id,
        mode: AnalyzerMode.POLLING,
        mode_before_crosspol: selectedAnalyzer.mode,
      };
      await updateAnalyzerMutation.mutateAsync({
        data: analyzerUpdate,
        analyzerId: analyzerUpdate.id,
      });
    };
    const createCrossPolSnapshot = async () => {
      if (!selectedSnapshot) return;
      const snapshotToSave: CreateSnapshotDto = {
        port: selectedSnapshot.port,
        switch_port: selectedSnapshot.switch_port,
        name: `${selectedSnapshot.name} CROSSPOL`,
        center: selectedSnapshot.center,
        span: selectedSnapshot.span,
        reference: selectedSnapshot.reference,
        scale: selectedSnapshot.scale,
        rbw: selectedSnapshot.rbw,
        vbw: selectedSnapshot.vbw,
        atten: selectedSnapshot.atten,
        preamp: selectedSnapshot.preamp,
        lo: selectedSnapshot.lo,
        lo_mode: selectedSnapshot.lo_mode,
        inverted: selectedSnapshot.inverted,
        detection_mode: selectedSnapshot.detection_mode,
        crosspol_snapshot_id: undefined,
        satellite_id: selectedSnapshot.satellite_id,
        analyzer_id: selectedSnapshot.analyzer_id,
        extra_wait_time: selectedSnapshot.extra_wait_time,
        enabled: true,
        crosspol_adjustment: 0,
      };
      return await createSnapshotMutation.mutateAsync({
        data: snapshotToSave,
      });
    };
    const saveCrossPolIdToPrimarySnapshot = async (savedSnapshot: Snapshot) => {
      if (!selectedSnapshot) return;
      await updateSnapshotMutation.mutateAsync({
        data: {
          crosspol_snapshot_id: savedSnapshot.id,
          enabled: true,
        },
        snapshotId: selectedSnapshot.id,
      });
    };
    const cleanUpCrossPolSnapshots = async () => {
      if (!selectedSnapshot || !selectedSnapshot.crosspol_snapshot_id) return;
      await deleteSnapshotMutation.mutateAsync(
        selectedSnapshot.crosspol_snapshot_id
      );
      await updateSnapshotMutation.mutateAsync({
        data: {
          crosspol_snapshot_id: null,
          enabled: geoEnabled ? undefined : false,
        },
        snapshotId: selectedSnapshot.id,
      });
    };
    const switchAnalyzerToLiveMode = async () => {
      if (!selectedAnalyzer) return;
      const analyzerUpdate = {
        id: selectedAnalyzer.id,
        mode: AnalyzerMode.LIVE,
      };
      await updateAnalyzerMutation.mutateAsync({
        data: analyzerUpdate,
        analyzerId: analyzerUpdate.id,
      });
    };
    const reEnableTemporarilyDisabledPollingSnapshots = () => {
      const snapshotsToReEnable =
        allSnapshotsForAnalyzer?.filter(
          (snapshot) => !!snapshot.temporarily_disabled
        ) || [];
      for (const snapshot of snapshotsToReEnable) {
        updateSnapshotMutation.mutate({
          snapshotId: snapshot.id,
          data: { enabled: true, temporarily_disabled: false },
        });
      }
    };
    const switchToCrossPol = async () => {
      const savedSnapshot = await createCrossPolSnapshot();
      if (!savedSnapshot || !user || !selectedAnalyzer || !selectedSnapshot)
        return;
      // Tell the analyzer about the new snapshot
      dispatch(
        updateAnalyzerProperties({
          user_id: user.id,
          properties: savedSnapshot,
          analyzer: selectedAnalyzer,
        })
      );
      if (selectedAnalyzer?.mode === AnalyzerMode.LIVE) {
        turnOffAllOtherPollingSnapshots(savedSnapshot);
      }
      await switchAnalyzerToPollingAndSavePriorMode();
      await saveCrossPolIdToPrimarySnapshot(savedSnapshot);
    };
    const switchFromCrossPol = async () => {
      await cleanUpCrossPolSnapshots();
      reEnableTemporarilyDisabledPollingSnapshots();
      if (
        selectedAnalyzer &&
        selectedAnalyzer?.mode_before_crosspol === AnalyzerMode.LIVE
      ) {
        await switchAnalyzerToLiveMode();
      }
    };

    if (selectedSnapshot && !selectedSnapshot.crosspol_snapshot_id) {
      await switchToCrossPol();
    } else {
      await switchFromCrossPol();
    }
  }, [
    selectedSnapshot,
    user,
    selectedAnalyzer,
    allSnapshotsForAnalyzer,
    snapshotId,
    updateSnapshotMutation,
    updateAnalyzerMutation,
    createSnapshotMutation,
    dispatch,
    deleteSnapshotMutation,
    geoEnabled,
  ]);
};

export default useHandleCrossPolClicked;
